import { useQuery } from '@tanstack/react-query';

import { apiUri } from '@/constants/Constants';
import { DealsDashboardClient } from '@/shared/api';

/** Re-export of `apiUri.inviteEligibility` for call sites / tests that import the path from this module. */
export const INVITE_ELIGIBILITY_PATH = apiUri.inviteEligibility;

export type InviteEligibilityOutcome = 'allow' | 'warning' | 'block';

export type InviteEligibilityMatch = {
  source: string;
  record_id: string;
  deal_id?: string | null;
  property_id?: string | null;
  address_id?: string | null;
  unit?: string;
  status_group: string;
  lease_end?: string | null;
  updated_at: string;
  contributed: boolean;
};

export type InviteEligibilityApplicant = {
  index: number;
  email: string;
  outcome: InviteEligibilityOutcome;
  reason: string;
  total_matches: number;
  truncated: boolean;
  matches: InviteEligibilityMatch[];
};

export type InviteEligibilityResponseData = {
  outcome: InviteEligibilityOutcome;
  match_sources?: string;
  applicants: InviteEligibilityApplicant[];
};

/**
 * Query params for GET /v1/invite/eligibility.
 * Emails are sent as repeated `emails=` keys (collectionFormat: multi).
 * `unit` is required on the invite form and is a DDS decision attribute.
 */
export type GetInviteEligibilityParams = {
  property_id: string;
  emails: string[];
  unit: string;
  address_id?: string;
  unit_id?: string;
  /** Invite being edited. Its own rows are left out so the invite does not match itself. */
  invite_alpha_id?: string;
};

type GetInviteEligibilityResponse = {
  message: string;
  status_code: number;
  data: InviteEligibilityResponseData;
};

const normalizeOptional = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

/** Stable, trimmed inputs for the query key so stale results drop when lease/emails change. */
export const normalizeInviteEligibilityParams = (
  params: GetInviteEligibilityParams,
): GetInviteEligibilityParams => ({
  property_id: params.property_id.trim(),
  // Lowercase so casing-only edits share one query key / eligibility request.
  emails: params.emails.map((email) => email.trim().toLowerCase()).filter(Boolean),
  unit: params.unit.trim(),
  address_id: normalizeOptional(params.address_id),
  unit_id: normalizeOptional(params.unit_id),
  invite_alpha_id: normalizeOptional(params.invite_alpha_id),
});

export const inviteEligibilityQueryKey = (params: GetInviteEligibilityParams) => {
  const normalized = normalizeInviteEligibilityParams(params);
  return [
    'invite-eligibility',
    normalized.property_id,
    normalized.address_id ?? '',
    normalized.unit,
    normalized.unit_id ?? '',
    normalized.emails,
    normalized.invite_alpha_id ?? '',
  ] as const;
};

/** Axios default array encoding uses `emails[]=`; DDS/Echo expect `emails=a&emails=b`. */
const serializeInviteEligibilityParams = (params: Record<string, unknown>): string => {
  const normalized = normalizeInviteEligibilityParams(params as GetInviteEligibilityParams);
  const searchParams = new URLSearchParams();
  searchParams.set('property_id', normalized.property_id);
  searchParams.set('unit', normalized.unit);
  if (normalized.address_id) {
    searchParams.set('address_id', normalized.address_id);
  }
  if (normalized.unit_id) {
    searchParams.set('unit_id', normalized.unit_id);
  }
  if (normalized.invite_alpha_id) {
    searchParams.set('invite_alpha_id', normalized.invite_alpha_id);
  }
  for (const email of normalized.emails) {
    searchParams.append('emails', email);
  }
  return searchParams.toString();
};

export const getInviteEligibility = async (
  params: GetInviteEligibilityParams,
): Promise<InviteEligibilityResponseData> => {
  const { data } = await DealsDashboardClient.get<GetInviteEligibilityResponse>(
    INVITE_ELIGIBILITY_PATH,
    {
      params: normalizeInviteEligibilityParams(params),
      paramsSerializer: serializeInviteEligibilityParams,
    },
  );
  return data.data;
};

export type UseInviteEligibilityParams = {
  propertyId: string | undefined;
  emails: string[];
  /** Required for DDS matching; query stays idle until non-empty. */
  unit: string | undefined;
  addressId?: string;
  unitId?: string;
  inviteAlphaId?: string;
  /**
   * Gate the request (Split flag, drawer open, etc.). Still requires property,
   * unit, and at least one email before a network call is made.
   */
  enabled?: boolean;
};

const buildInviteEligibilityParams = ({
  propertyId,
  emails,
  addressId,
  unit,
  unitId,
  inviteAlphaId,
}: Omit<UseInviteEligibilityParams, 'enabled'>): GetInviteEligibilityParams | undefined => {
  const trimmedUnit = unit?.trim() || undefined;
  if (!propertyId || !trimmedUnit) {
    return undefined;
  }
  return {
    property_id: propertyId,
    emails,
    unit: trimmedUnit,
    address_id: addressId,
    unit_id: unitId,
    invite_alpha_id: inviteAlphaId,
  };
};

const canRunInviteEligibilityQuery = (
  normalized: GetInviteEligibilityParams | undefined,
): boolean => Boolean(normalized?.property_id && normalized.unit && normalized.emails.length > 0);

export const useInviteEligibility = ({
  propertyId,
  emails,
  addressId,
  unit,
  unitId,
  inviteAlphaId,
  enabled = true,
}: UseInviteEligibilityParams) => {
  const params = buildInviteEligibilityParams({
    propertyId,
    emails,
    addressId,
    unit,
    unitId,
    inviteAlphaId,
  });
  const normalized = params ? normalizeInviteEligibilityParams(params) : undefined;
  const canQuery = canRunInviteEligibilityQuery(normalized);

  return useQuery<InviteEligibilityResponseData>({
    queryKey: normalized
      ? inviteEligibilityQueryKey(normalized)
      : (['invite-eligibility', 'idle'] as const),
    queryFn: () => {
      if (!normalized) {
        return Promise.reject(new Error('Invite eligibility params are missing'));
      }
      return getInviteEligibility(normalized);
    },
    enabled: enabled && canQuery,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
